import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { toast } from '@/hooks/use-toast';

const EMAIL_SIGNATURES_QUERY_KEY = ['/api/email/signatures'] as const;
const EMAIL_SIGNATURES_ENDPOINT = '/api/email/signatures';

// Definizione dell'interfaccia per la firma email
export interface EmailSignature {
  id: number;
  name: string;
  content: string;
  isDefault: boolean;
  isAccountDefault?: boolean;
  isDefaultNew?: boolean;
  isDefaultReply?: boolean;
  createdAt: string;
  updatedAt: string;
}

// Interfaccia per la creazione di una firma
export interface CreateSignatureData {
  name: string;
  content: string;
  isDefault?: boolean;
}

// Hook per gestire le firme email
export const useEmailSignatures = (accountId?: number | null) => {
  const queryClient = useQueryClient();
  const [isSettingDefault, setIsSettingDefault] = useState(false);

  // Query per ottenere tutte le firme
  const { data: signatures = [], isLoading, error } = useQuery<EmailSignature[]>({
    queryKey: accountId
      ? ['/api/email/accounts', accountId, 'signature-defaults']
      : EMAIL_SIGNATURES_QUERY_KEY,
    queryFn: async () => {
      if (!accountId) return apiRequest(EMAIL_SIGNATURES_ENDPOINT);

      const response = await apiRequest('GET', `/api/email/accounts/${accountId}/signature-defaults`);
      return Array.isArray(response?.signatures) ? response.signatures : [];
    },
  });

  // Mutation per creare una nuova firma
  const createSignatureMutation = useMutation({
    mutationFn: async (data: CreateSignatureData) => {
      return await apiRequest('POST', EMAIL_SIGNATURES_ENDPOINT, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAIL_SIGNATURES_QUERY_KEY });
      toast({
        title: 'Firma creata',
        description: 'La firma è stata creata con successo.',
      });
    },
    onError: (error) => {
      console.error('Errore durante la creazione della firma:', error);
      toast({
        title: 'Errore',
        description: 'Si è verificato un errore durante la creazione della firma.',
        variant: 'destructive',
      });
    },
  });

  // Mutation per aggiornare una firma esistente
  const updateSignatureMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateSignatureData> }) => {
      return await apiRequest('PATCH', `${EMAIL_SIGNATURES_ENDPOINT}/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAIL_SIGNATURES_QUERY_KEY });
      toast({
        title: 'Firma aggiornata',
        description: 'La firma è stata aggiornata con successo.',
      });
    },
    onError: (error) => {
      console.error('Errore durante l\'aggiornamento della firma:', error);
      toast({
        title: 'Errore',
        description: 'Si è verificato un errore durante l\'aggiornamento della firma.',
        variant: 'destructive',
      });
    },
  });

  // Mutation per eliminare una firma
  const deleteSignatureMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest('DELETE', `${EMAIL_SIGNATURES_ENDPOINT}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAIL_SIGNATURES_QUERY_KEY });
      toast({
        title: 'Firma eliminata',
        description: 'La firma è stata eliminata con successo.',
      });
    },
    onError: (error) => {
      console.error('Errore durante l\'eliminazione della firma:', error);
      toast({
        title: 'Errore',
        description: 'Si è verificato un errore durante l\'eliminazione della firma.',
        variant: 'destructive',
      });
    },
  });

  // Funzione per impostare una firma come predefinita
  const setDefaultSignature = async (id: number) => {
    setIsSettingDefault(true);
    try {
      await apiRequest('PATCH', `${EMAIL_SIGNATURES_ENDPOINT}/${id}/default`);
      queryClient.invalidateQueries({ queryKey: EMAIL_SIGNATURES_QUERY_KEY });

      toast({
        title: 'Firma predefinita aggiornata',
        description: 'La firma predefinita è stata aggiornata con successo.',
      });
    } catch (error) {
      console.error('Errore durante l\'impostazione della firma predefinita:', error);
      toast({
        title: 'Errore',
        description: 'Si è verificato un errore durante l\'impostazione della firma predefinita.',
        variant: 'destructive',
      });
    } finally {
      setIsSettingDefault(false);
    }
  };

  return {
    signatures,
    isLoading,
    error,
    createSignature: createSignatureMutation.mutate,
    updateSignature: updateSignatureMutation.mutate,
    deleteSignature: deleteSignatureMutation.mutate,
    setDefaultSignature,
    isCreating: createSignatureMutation.isPending,
    isUpdating: updateSignatureMutation.isPending,
    isDeleting: deleteSignatureMutation.isPending,
    isSettingDefault,
  };
};
